import React, { useState, useEffect, forwardRef } from 'react';
import { EventItem } from '../../types/event';
import { getEvents } from '../../services/eventService';
import EventSummaryCard from '../common/EventSummaryCard';
import EventSkeleton from './EventSkeleton';
import { logger } from '../../utils/logger';
import { useAuth } from '../../context/AuthContext';
import '../../styles/EventsSection.css';

interface EventsSectionProps {
  title?: string;
  maxEvents?: number;
  className?: string;
  fallbackMessage?: string;
}

const EventsSection = forwardRef<HTMLElement, EventsSectionProps>(({ 
  title = "Our Events", 
  maxEvents = 3,
  className = "",
  fallbackMessage = "No upcoming events at the moment. Check back soon!"
}, ref) => {
  const { currentUser } = useAuth();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        logger.info('🔄 Loading events for homepage display', { component: 'EventsSection' });
        const eventsData = await getEvents();
        
        // Sort by date and filter upcoming events
        const now = new Date();
        now.setHours(0, 0, 0, 0); // Reset hours to start of day for fair comparison
        
        const upcomingEvents = eventsData
          .filter(event => {
            const eventDate = new Date(event.date);
            eventDate.setHours(0, 0, 0, 0); // Reset hours to start of day
            
            // Debug logging for today's events
            const isToday = eventDate.toDateString() === now.toDateString();
            const isUpcoming = eventDate >= now;
            
            logger.debug('📅 Event date comparison', {
              eventDate: eventDate.toISOString(),
              today: now.toISOString(),
              eventDateString: eventDate.toDateString(),
              todayString: now.toDateString(),
              isToday: isToday,
              isUpcoming: isUpcoming,
              willShow: isUpcoming
            });
            
            return isUpcoming; // Include today's events
          })
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const eventsToShow = upcomingEvents.slice(0, maxEvents);
        setEvents(eventsToShow);
        logger.success('✅ Events loaded successfully', { 
          component: 'EventsSection', 
          count: eventsToShow.length,
          total: eventsData.length,
          today: now.toDateString()
        });
      } catch (error) {
        logger.error('❌ Failed to load events:', error);
        setError('Unable to load upcoming events. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [maxEvents]);

  // Format the date for display
  const formatEventDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format the time for display
  const formatEventTime = (event: EventItem): string => {
    return `${event.startTime.doorsOpen} - ${event.endTime || 'TBD'}`;
  };

  // Get the theme from description (this could be more sophisticated in a real app)
  const getEventTheme = (description: string): string => {
    if (description.toLowerCase().includes('play-party')) return 'Play-Party';
    if (description.toLowerCase().includes('workshop')) return 'Workshop';
    if (description.toLowerCase().includes('social')) return 'Social';
    return 'Event';
  };

  return (
    <section className={`events-section container ${className}`} ref={ref}>
      <h2 className="text-center">{title}</h2>
      
      {loading && (
        <div className="events-loading">
          <EventSkeleton count={maxEvents} />
        </div>
      )}
      
      {error && (
        <div className="events-error">
          <p>{error}</p>
        </div>
      )}
      
      {!loading && !error && events.length === 0 && (
        <div className="no-events">
          <p>{fallbackMessage}</p>
        </div>
      )}
      
      {!loading && !error && events.length > 0 && (
        <div className="event-grid">
          {events.map(event => (
            <EventSummaryCard
              key={event.id}
              title={event.title}
              description={event.description}
              imageSrc={event.imageUrl || '/images/room_events.jpg'} // Fallback image
              theme={getEventTheme(event.description)}
              linkHref={`/events/${event.id}`} // Link to individual event page
              linkText={`Join us on ${formatEventDate(event.date)}`}
              date={formatEventDate(event.date)}
              time={formatEventTime(event)}
              location={event.address}
              eventId={event.id}
              currentUser={currentUser}
            />
          ))}
        </div>
      )}
      
      {!loading && !error && events.length > 0 && (
        <div className="view-all-events">
          <a href="/events" className="view-all-link">
            View all upcoming events <span className="arrow">→</span>
          </a>
        </div>
      )}
    </section>
  );
});

export default EventsSection;
